import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Select, DatePicker, Button, Table, Tag, message, Space, Form, Tooltip, InputNumber, Input, Checkbox,
  Segmented,
} from 'antd';
import { DownloadOutlined, MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import ResizableTitle from '../components/ResizableTitle';
import NoCallEvalModal from '../components/NoCallEvalModal';
import { loadPageCache, savePageCache } from '../utils/pageCache';

const { RangePicker } = DatePicker;

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Rescore Requested',
  canceled:          'Canceled',
};

// Nothing in the app ever sets in_progress -- it is read in filters and
// groupings but never written by any insert or update -- so no case can have
// it and it is not offered as a filter. Its label stays above so that a legacy
// row, if one exists, still renders readably instead of as a raw value.
const RETIRED_STATUSES = new Set(['in_progress']);

// Derived rather than kept as a second hand-written list. The two had drifted,
// which is why in_progress was in the default filter but had no option to match
// it: Ant then showed the raw value as a tag, and removing that tag made it
// unselectable.
const STATUS_OPTIONS = Object.entries(STATUS_LABEL)
  .filter(([value]) => !RETIRED_STATUSES.has(value))
  .map(([value, label]) => ({ label, value }));

// What a fresh search starts on, run through the same retired filter as the
// options. There were three copies of this list and two of them still named
// in_progress, so the default selected a status with no option to match it and
// Ant rendered the raw value as a tag.
const DEFAULT_STATUSES = ['assigned', 'in_progress', 'rescore_requested']
  .filter((value) => !RETIRED_STATUSES.has(value));

// Quick toggles above the filters (as on Help Desk), each searching at once.
// Status: the toggle (Open by default) unless statuses are picked in the
// dropdown, which then win; a toggle click clears the dropdown. Open = Draft
// and Rescore Requested, the old default. Assigned: Anyone or Me (every
// evaluation has an assignee, so there is no Unassigned).
const STATUS_PRESETS = { open: DEFAULT_STATUSES, completed: ['completed'], all: [] };
const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

function formatDuration(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Serialize form values for sessionStorage (dayjs → ISO string)
function _serializeFormValues(f) {
  const v = { ...f };
  if (v.dateRange)        v.dateRange        = v.dateRange.map((d) => d?.toISOString?.() ?? d);
  if (v.evalCreatedRange) v.evalCreatedRange = v.evalCreatedRange.map((d) => d?.toISOString?.() ?? d);
  return v;
}

// Acknowledgement deadline (YYYY-MM-DD, Eastern): the completed date plus the
// form's dispute window. Only completed evaluations have one.
const ackDeadline = (c) => (c.Status === 'completed' && c.CompletedAt
  ? dayjs(formatTS(c.CompletedAt, 'YYYY-MM-DD')).add(c.DisputeWindowDays ?? 4, 'day').format('YYYY-MM-DD')
  : null);

// Missed Acknowledgement Deadline: 1 when there is a deadline and the evaluation was
// acknowledged after it, or is still not acknowledged and today is after it.
// Dates are Eastern calendar days, so acknowledging on the due day is on time.
const pastDueAck = (c) => {
  const dl = ackDeadline(c);
  if (!dl) return 0;
  const acked = c.AcknowledgedAt ? formatTS(c.AcknowledgedAt, 'YYYY-MM-DD') : null;
  if (acked) return acked > dl ? 1 : 0;
  return formatTS(new Date().toISOString(), 'YYYY-MM-DD') > dl ? 1 : 0;
};

export default function CasesPage() {
  const navigate    = useNavigate();
  const currentUser = React.useContext(UserContext);
  const isAgent = currentUser?.Role === 'agent';

  // New Evaluation (no call): shown only when some form is ticked "Allow
  // without a call" on a campaign this user can see.
  const [noCall, setNoCall]         = useState({ forms: [], agents: [] });
  const [noCallOpen, setNoCallOpen] = useState(false);
  useEffect(() => {
    if (isAgent || (currentUser?.CanEditEvals ?? 0) < 2) return;
    api.get('/api/cases/no-call-options').then((r) => setNoCall(r.data)).catch(() => {});
  }, [isAgent, currentUser?.CanEditEvals]);

  const _cache = loadPageCache('cases');
  const restoredRef = useRef(!!_cache);
  const [cases, setCases]               = useState(_cache?.cases ?? []);
  const [loading, setLoading]           = useState(false);
  const [agentOptions, setAgentOptions]       = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [formOptions, setFormOptions]         = useState([]);
  const [userOptions, setUserOptions]         = useState([]);
  // Filters remembered from earlier in the tab's session (sessionStorage) can
  // predate the toggles: a status list that is just what a toggle means -- the
  // old default, or one still naming the retired in_progress -- becomes that
  // toggle, so the dropdown is left empty and the toggle lit.
  const _cachedStatus = (_cache?.filters?.status || []).filter((v) => !RETIRED_STATUSES.has(v));
  const _cachedPreset = _cachedStatus.length
    ? Object.keys(STATUS_PRESETS).find((k) => STATUS_PRESETS[k].length && sameSet(STATUS_PRESETS[k], _cachedStatus))
    : null;
  const _oldDefault = !!_cachedPreset;
  const _restored = _cache?.filters
    ? { ..._cache.filters, status: _oldDefault ? [] : _cachedStatus,
        status_mode: _cachedPreset || _cache.filters.status_mode || 'open' }
    : null;
  const [filters, setFilters]           = useState(_restored || { status: [] });
  const [statusMode, setStatusMode] = useState(_restored?.status_mode ?? 'open');
  const [colWidths, setColWidths]       = useState({});
  const [scoreRange, setScoreRange]         = useState([null, null]);

  const [form] = Form.useForm();
  const notAcknowledgedWatch = Form.useWatch('not_acknowledged', form);

  // Restore form fields from cache (date strings → dayjs)
  useEffect(() => {
    if (_cache?.formValues) {
      // Restore cached form values (date strings → dayjs)
      const v = { ..._cache.formValues };
      if (v.dateRange)         v.dateRange         = v.dateRange.map((d) => d ? dayjs(d) : null);
      if (v.evalCreatedRange)  v.evalCreatedRange  = v.evalCreatedRange.map((d) => d ? dayjs(d) : null);
      v.status = _restored.status;
      form.setFieldsValue(v);
    } else {
      // No cache: call date defaults to the current month; status comes from
      // the Open toggle.
      form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
    }
  }, []); // eslint-disable-line

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/agent-stats/options')
      .then((r) => {
        setCampaignOptions(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setAgentOptions(r.data.agents.map((a) => ({ label: a.name, value: a.id ?? a.name })));
      })
      .catch(() => {});
    api.get('/api/eval-forms/names')
      .then((r) => setFormOptions(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
    // Who has actually been assigned an evaluation here, not who could be
    // assigned one today. A search page looks backwards: someone who has
    // left, changed team or lost the permission still has months of work
    // behind them, and the old list dropped them -- silently, since the
    // name was simply absent rather than empty.
    api.get('/api/cases/assigned-to-options')
      .then((r) => setUserOptions((Array.isArray(r.data) ? r.data : []).map((u) => ({
        // Marked rather than hidden: their evaluations are still there.
        label: (u.DisplayName || u.Email) + (u.IsActive ? '' : ' (inactive)'),
        value: u.ID,
      }))))
      .catch(() => {});
  }, []);

  const fetchCases = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      // Eval ID or Contact ID: find that record whatever the other filters
      // (dates, status ...) say -- they are what made it hard to pull up.
      const idOnly = (f.case_id || '').toString().trim() || (f.contact_id || '').toString().trim();
      if (idOnly) {
        const p = {};
        if ((f.case_id || '').toString().trim())    p.case_id    = f.case_id.toString().trim().replace(/^#/, '');
        if ((f.contact_id || '').toString().trim()) p.contact_id = f.contact_id.toString().trim();
        const res = await api.get('/api/cases', { params: p });
        setCases(res.data);
        savePageCache('cases', { cases: res.data, filters: f, formValues: _serializeFormValues(f) });
        return;
      }
      const params = {};
      if (f.agent_user_id != null) {
        params.agent_user_id = f.agent_user_id;
      }
      if (f.external_ref)           params.external_ref        = f.external_ref;
      if (f.assigned_to_user_id)    params.assigned_to_user_id = f.assigned_to_user_id;
      // '0' is a real choice, so a plain truthiness test would drop it.
      if (f.created_by_ai != null && f.created_by_ai !== '') params.created_by_ai = f.created_by_ai;
      // Picked statuses win; otherwise the toggle's (All = none).
      const st = f.status?.length ? f.status : (STATUS_PRESETS[f.status_mode || 'open'] || []);
      if (st.length)                params.status              = st.join(',');
      if (f.campaign?.length) params.campaign   = f.campaign.join(',');
      if (f.form_name?.length) params.form_name  = f.form_name.join(',');
      // Filters replayed from the session cache have been through JSON, so
      // dayjs objects come back as strings and .format() would throw. Wrapping
      // in dayjs() accepts either a live object or a restored string.
      const fmt = (v) => (v ? dayjs(v).format('YYYY-MM-DD') : undefined);
      if (f.dateRange?.[0])        params.start_date         = fmt(f.dateRange[0]);
      if (f.dateRange?.[1])        params.end_date           = fmt(f.dateRange[1]);
      if (f.evalCreatedRange?.[0]) params.eval_created_start = fmt(f.evalCreatedRange[0]);
      if (f.evalCreatedRange?.[1]) params.eval_created_end   = fmt(f.evalCreatedRange[1]);
      if (f.not_acknowledged)      params.not_acknowledged   = '1';

      const res = await api.get('/api/cases', { params });
      setCases(res.data);
      savePageCache('cases', { cases: res.data, filters: f, formValues: _serializeFormValues(f) });
    } catch {
      message.error('Failed to load evaluations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { restoredRef.current = false; }, []); // eslint-disable-line
  useEffect(() => { if (_cache) fetchCases(_restored || {}); }, []); // eslint-disable-line

  const handleSearch = (values, over = {}) => {
    const f = { ...values, status_mode: statusMode, ...over };
    setFilters(f);
    fetchCases(f);
  };
  const pickStatus = (k) => {
    setStatusMode(k);
    form.setFieldsValue({ status: [] });
    handleSearch(form.getFieldsValue(), { status: [], status_mode: k });
  };
  const assignedWatch = Form.useWatch('assigned_to_user_id', form);
  const pickAssigned = (k) => {
    const id = k === 'me' ? currentUser?.ID : undefined;
    form.setFieldsValue({ assigned_to_user_id: id });
    handleSearch({ ...form.getFieldsValue(), assigned_to_user_id: id });
  };
  const statusWatch = Form.useWatch('status', form);

  const scorePct = (c) =>
    c.TotalPossible > 0 ? Math.round(c.TotalEarned / c.TotalPossible * 1000) / 10 : null;

  const displayedCases = cases.filter((c) => {
    const [sMin, sMax] = scoreRange;
    if (sMin != null || sMax != null) {
      const pct = scorePct(c);
      if (sMin != null && (pct == null || pct < sMin)) return false;
      if (sMax != null && (pct == null || pct > sMax)) return false;
    }
    return true;
  });

  const exportToExcel = () => {
    const rows = displayedCases.map((c) => ({
      'Eval ID':              c.ID,
      'Contact ID':           c.ContactID,
      'External Ref':         c.ExternalRef ?? '',
      'Status':               STATUS_LABEL[c.Status] ?? c.Status,
      'Agent':                c.AgentName ?? '',
      'Campaign':             c.Campaign ?? '',
      'Skill':                c.Skill ?? '',
      'Call Date':            c.CallDate?.slice(0, 10) ?? '',
      'Eval Form':            c.FormName ? `${c.FormName}${c.FormVersion ? ` v${c.FormVersion}` : ''}` : '',
      'Assigned To':          c.AssignedToName ?? '',
      'Score':                c.TotalPossible > 0 ? c.TotalEarned : '',
      'Total Pts Possible':   c.TotalPossible > 0 ? c.TotalPossible : '',
      'Feedback':             c.Feedback ?? '',
      'Silver %':             c.GoalSilverPct     ?? '',
      'Target (Gold) %':      c.GoalTargetPct     ?? '',
      'Platinum %':           c.GoalPlatinumPct   ?? '',
      'Duration':             c.AgentTime != null ? `${Math.floor(c.AgentTime / 60)}:${String(c.AgentTime % 60).padStart(2, '0')}` : '',
      'Last Modified':        formatTS(c.UpdatedAt),
      'Created On':           formatTS(c.CreatedAt),
      'Completed On':         formatTS(c.CompletedAt),
      'Acknowledged On':      c.AcknowledgedAt ? formatTS(c.AcknowledgedAt) : '',
      'Acknowledge Deadline': ackDeadline(c) ?? '',
      'Missed Acknowledgement Deadline': pastDueAck(c),
      'Rescore Requested On': formatTS(c.RescoreRequestedAt),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Evaluations');
    XLSX.writeFile(wb, 'evaluations.xlsx');
  };

  const columns = [
    {
      title: 'ID', dataIndex: 'ID', key: 'ID', width: 80,
      sorter: (a, b) => a.ID - b.ID,
      render: (v) => <Typography.Text strong style={{ color: '#1677ff' }}>#{v}</Typography.Text>,
    },
    {
      title: 'Contact ID',
      dataIndex: 'ContactID',
      key: 'ContactID',
      width: 110,
    },
    {
      title: 'Call Date',
      dataIndex: 'CallDate',
      key: 'CallDate',
      width: 110,
      render: (v) => v?.slice(0, 10) ?? '-',
      sorter: (a, b) => (a.CallDate ?? '').localeCompare(b.CallDate ?? ''),
    },
    {
      title: 'External Ref',
      dataIndex: 'ExternalRef',
      key: 'ExternalRef',
      width: 130,
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 160,
      render: (status) => (
        <Tag color={STATUS_COLOR[status] ?? 'default'}>
          {STATUS_LABEL[status] ?? status}
        </Tag>
      ),
    },
    {
      title: 'Assigned To',
      dataIndex: 'AssignedToName',
      key: 'AssignedToName',
      width: 150,
    },
    {
      title: 'Agent',
      dataIndex: 'AgentName',
      key: 'AgentName',
      width: 150,
      render: (name, record) => name
        ? (
          (currentUser?.CanViewAgentSummary ?? 0) < 1
            ? name
            : <Typography.Link
                onClick={(e) => {
                  e.stopPropagation();
                  // Identified by user, with AgentNo only for rows that
                  // predate AgentUserID. Never by name.
                  const q = record.AgentUserID
                    ? `user_id=${record.AgentUserID}`
                    : `agent_no=${encodeURIComponent(record.AgentNo)}`;
                  navigate(`/agents?${q}`);
                }}
              >
                {name}
              </Typography.Link>
        )
        : '—',
    },
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
    },
    {
      title: 'Form',
      dataIndex: 'FormName',
      key: 'FormName',
      ellipsis: true,
      render: (name, record) =>
        name
          ? `${name}${record.FormVersion ? ` v${record.FormVersion}` : ''}`
          : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Skill',
      dataIndex: 'Skill',
      key: 'Skill',
    },
    {
      title: 'Score',
      key: 'Score',
      width: 120,
      sorter: (a, b) => (scorePct(a) ?? -1) - (scorePct(b) ?? -1),
      render: (_, c) => {
        const pct = scorePct(c);
        if (pct == null) return <Typography.Text type="secondary">—</Typography.Text>;
        const color = pct >= 90 ? 'green' : pct >= 70 ? 'orange' : 'red';
        return (
          <Space size={4}>
            <Tag color={color} style={{ marginRight: 0 }}>{pct}%</Tag>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>{c.TotalEarned}/{c.TotalPossible}</Typography.Text>
          </Space>
        );
      },
    },
    {
      // The form's Feedback question (tagged in the form editor), on hover.
      title: 'Feedback',
      key: 'Feedback',
      width: 90,
      align: 'center',
      render: (_, c) => (c.Feedback?.trim()
        ? (
          <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{c.Feedback}</div>}
            overlayStyle={{ maxWidth: 500 }}>
            <MessageOutlined style={{ color: '#1677ff', fontSize: 16 }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        )
        : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Completed On',
      dataIndex: 'CompletedAt',
      key: 'CompletedAt',
      width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.CompletedAt ?? '').localeCompare(b.CompletedAt ?? ''),
    },
    {
      // The latest coaching session raised from the evaluation, as on the
      // Error search.
      title: 'Coaching', key: 'Coaching', width: 160,
      sorter: (a, b) => (a.CoachingStatus ?? '').localeCompare(b.CoachingStatus ?? ''),
      render: (_, r) => {
        if (!r.CoachingStatus) return null;
        const COLOR = { draft: 'default', active: 'blue', completed: 'green' };
        const LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed' };
        return (
          <Space size={4} direction="vertical" style={{ gap: 2 }}>
            {r.CoachingRequestType && (
              <Typography.Text style={{ fontSize: 12 }}>{r.CoachingRequestType}</Typography.Text>
            )}
            <Tag color={COLOR[r.CoachingStatus] ?? 'default'} style={{ margin: 0 }}>
              {LABEL[r.CoachingStatus] ?? r.CoachingStatus}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Acknowledged Date',
      dataIndex: 'AcknowledgedAt',
      key: 'AcknowledgedAt',
      width: 140,
      sorter: (a, b) => (a.AcknowledgedAt || '').localeCompare(b.AcknowledgedAt || ''),
      render: (v) => v
        ? <span style={{ color: '#8c8c8c', fontSize: 12 }}>{formatTS(v)}</span>
        : null,
    },
    {
      title: 'Acknowledge Deadline',
      key: 'DisputeDeadline',
      width: 130,
      render: (_, r) => {
        const dl = ackDeadline(r);
        if (!dl) return null;
        const passed = formatTS(new Date().toISOString(), 'YYYY-MM-DD') > dl;
        return (
          <span style={{ color: passed ? '#8c8c8c' : '#fa8c16', fontSize: 12 }}>
            {dl}
          </span>
        );
      },
      sorter: (a, b) => {
        if (a.Status !== 'completed' || !a.CompletedAt) return 1;
        if (b.Status !== 'completed' || !b.CompletedAt) return -1;
        return dayjs(a.CompletedAt).add(a.DisputeWindowDays ?? 4, 'day')
          .diff(dayjs(b.CompletedAt).add(b.DisputeWindowDays ?? 4, 'day'));
      },
    },
    {
      title: 'Missed Acknowledgement Deadline',
      key: 'PastDueAck',
      width: 120,
      align: 'center',
      filters: [{ text: '1', value: 1 }, { text: '0', value: 0 }],
      onFilter: (v, r) => pastDueAck(r) === v,
      sorter: (a, b) => pastDueAck(a) - pastDueAck(b),
      render: (_, r) => (pastDueAck(r)
        ? <Typography.Text type="danger" strong>1</Typography.Text>
        : <span style={{ color: '#8c8c8c' }}>0</span>),
    },
    {
      title: 'Rescore Requested On',
      dataIndex: 'RescoreRequestedAt',
      key: 'RescoreRequestedAt',
      width: 190,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.RescoreRequestedAt ?? '').localeCompare(b.RescoreRequestedAt ?? ''),
    },
    {
      title: 'Duration',
      dataIndex: 'AgentTime',
      key: 'AgentTime',
      width: 90,
      render: formatDuration,
    },
    {
      title: 'Last Modified',
      dataIndex: 'UpdatedAt',
      key: 'UpdatedAt',
      width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.UpdatedAt ?? '').localeCompare(b.UpdatedAt ?? ''),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Created On',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.CreatedAt ?? '').localeCompare(b.CreatedAt ?? ''),
    },
  ].map(toResizable);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Evaluations
        </Typography.Title>
        {noCall.forms.length > 0 && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNoCallOpen(true)}>New Evaluation</Button>
        )}
      </div>
      <NoCallEvalModal open={noCallOpen} forms={noCall.forms} agents={noCall.agents} currentUser={currentUser}
        onClose={() => setNoCallOpen(false)} onCreated={(id) => id && navigate(`/cases/${id}`)} />

      <Space wrap size={16} style={{ marginBottom: 12 }}>
        <Segmented
          value={statusWatch?.length ? null : statusMode}
          onChange={pickStatus}
          options={[{ value: 'open', label: 'Open' }, { value: 'completed', label: 'Completed' }, { value: 'all', label: 'All' }]}
        />
        {!isAgent && (
          <Segmented
            value={assignedWatch == null ? 'any' : (assignedWatch === currentUser?.ID ? 'me' : null)}
            onChange={pickAssigned}
            options={[{ value: 'any', label: 'Anyone' }, { value: 'me', label: 'Assigned to Me' }]}
          />
        )}
      </Space>

      <Form
        form={form}
        layout="inline"
        onFinish={(values) => handleSearch(values)}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        {!isAgent && (
          <Form.Item name="agent_user_id" label="Agent">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All agents"
              options={agentOptions}
              style={{ width: 200 }}
            />
          </Form.Item>
        )}

        <Form.Item name="case_id" label="Eval ID">
          <Input allowClear placeholder="e.g. 363" style={{ width: 110 }} />
        </Form.Item>

        <Form.Item name="contact_id" label="Contact ID">
          <Input allowClear style={{ width: 150 }} />
        </Form.Item>

        <Form.Item name="external_ref" label="External Ref">
          <Input allowClear placeholder="Search…" style={{ width: 160 }} />
        </Form.Item>

        {!isAgent && (
          <Form.Item name="assigned_to_user_id" label="Assigned To">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All users"
              // Assigned to Me picks the current user, who may not have had an
              // evaluation assigned yet.
              options={!currentUser || userOptions.some((o) => o.value === currentUser.ID) ? userOptions
                : [{ label: currentUser.DisplayName, value: currentUser.ID }, ...userOptions]}
              style={{ width: 200 }}
            />
          </Form.Item>
        )}

        <Form.Item name="created_by_ai" label="Created By">
          <Select
            allowClear
            placeholder="Anyone"
            style={{ width: 150 }}
            options={[
              { label: 'AI', value: '1' },
              { label: 'A person', value: '0' },
            ]}
          />
        </Form.Item>

        <Form.Item name="dateRange" label="Call Date">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="evalCreatedRange" label="Eval Created">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="status" label="Status">
          <Select
            mode="multiple"
            allowClear
            placeholder="Specific statuses"
            options={STATUS_OPTIONS}
            maxTagCount="responsive"
            style={{ width: 320 }}
          />
        </Form.Item>

        <Form.Item name="not_acknowledged" valuePropName="checked">
          <Checkbox>Not Acknowledged</Checkbox>
        </Form.Item>

        <Form.Item name="campaign" label="Campaign">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All campaigns"
            options={campaignOptions}
            maxTagCount="responsive"
            style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item name="form_name" label="Eval Form">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All forms"
            options={formOptions}
            maxTagCount="responsive"
            style={{ width: 220 }}
          />
        </Form.Item>

        <Form.Item label="Score %">
          <Space.Compact>
            <InputNumber
              placeholder="Min"
              min={0} max={100} step={1}
              value={scoreRange[0]}
              onChange={(v) => setScoreRange(([, mx]) => [v, mx])}
              style={{ width: 72 }}
            />
            <InputNumber
              placeholder="Max"
              min={0} max={100} step={1}
              value={scoreRange[1]}
              onChange={(v) => setScoreRange(([mn]) => [mn, v])}
              style={{ width: 72 }}
            />
          </Space.Compact>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Filter
            </Button>
            <Button
              onClick={() => {
                const defaultFilters = { status: [], status_mode: 'open' };
                form.resetFields();
                form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
                setStatusMode('open');
                setFilters(defaultFilters);
                setScoreRange([null, null]);
                fetchCases(defaultFilters);
              }}
            >
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={cases.length === 0}
            >
              Export
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="ID"
        dataSource={displayedCases}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} evaluations` }}
        onRow={(record) => ({
          onClick: () => navigate(`/cases/${record.ID}`),
          style: { cursor: 'pointer' },
        })}
      />
    </>
  );
}
